import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './index.css';
import ReactCodeInput from 'react-code-input';
import { Spin } from 'antd';
import { LoadingOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import api_config from 'lib/api/common';
import UseAPI from 'lib/api/UseAPI';
import validator from 'validator';
import Logo from '../../assets/img/logo.png';
import LPPromoItem1 from './LPPromoItem1';
import { FormContainer, FormInputField, ResetButton, TermsText, PasswordInputContainer, PasswordToggleIcon } from './AuthStyles';
import AuthContainer from './AuthContainer';
import useCustomNotification from '../../hooks/useCustomNotification';
import NotificationProvider from '../../components/CustomNotification/NotificationProvider';

const antIcon = (
  <LoadingOutlined
    style={{
      fontSize: 24,
    }}
    spin
  />
);

const Reset = () => {
  const { notifications, removeNotification, api } = useCustomNotification();
  const openNotificationWithIcon = (type, message, desc) => {
    api[type](message, desc);
  };

  const navigate = useNavigate();

  const [pwd, setPwd] = useState('');
  const [matchPwd, setMatchPwd] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showMatchPassword, setShowMatchPassword] = useState(false);
  const [isEnableSignUp, setEnableSignUp] = useState(false);
  const [isVerifyLoading, setVerifyLoading] = useState(false);
  const [isSubmitLoading, setSubmitLoading] = useState(false);
  const [isUserLoading, setUserloading] = useState(false);

  const [user, setUser] = useState('');
  const [isDisableduser, setDisableUser] = useState(false);
  const [isVerifyStatus, setVerifyCodeStatus] = useState(false);

  const [code, setCode] = useState('');
  const [isEnablecode, setEnableCode] = useState(false);
  const [isEnableSignUpAPI, setEnableAPISignUp] = useState(false);

  const [errMsg, setErrMsg] = useState('');

  const handleChange = (e) => {
    setUser(e.target.value);
  };

  const handleCode = (value) => {
    setCode(value);
  };

  let oldTitle = document.title;
  useEffect(() => {
    document.title = "Reset Password | Sayify";
    return () => {
      document.title = oldTitle;
    };
  }, []);

  const handleAPISubmit = async () => {
    setUserloading(true);
    let payload;
    if (validator.isEmail(user)) {
      payload = {
        email: user,
        signup: false,
      };
    } else {
      setUserloading(false);
      openNotificationWithIcon('error', 'Enter a valid email', 'A valid email address is required');
      return;
    }
    let url = api_config.BASE_BACKEND_ENDPOINT + 'api/send-verification-code/';
    api_config.ABORT_CONTROLLER = new AbortController();
    const signal = api_config.ABORT_CONTROLLER.signal;

    try {
      UseAPI(signal, url, 'POST', payload)
        .then(function (response) {
          if (response.code === 410) {
            openNotificationWithIcon(
              'error',
              'Failed to send verification code',
              'No user exists with this email address. Please sign up to create a new account. ',
            );
          }
          else if (response.code === 500) {
            openNotificationWithIcon(
              'error',
              'Failed to send verification code',
              'The verification code was not sent due to some server error. Please retry again later. If the issue persists, contact support@sayify.pro',
            );
          }
          else if (response.code === 200) {
            openNotificationWithIcon(
              'success',
              'Verification code sent',
              'We have sent a verification code to your email. Please check your inbox ',
            );

            setVerifyCodeStatus(true);
            setDisableUser(true);
            setEnableCode(true);
            setSubmitLoading(false);
            setVerifyLoading(true);
          }

          setUserloading(false);
        })
        .catch(function (err) {
          setUserloading(false);
        });
    } catch (err) {
      openNotificationWithIcon(
        'error',
        'Failed to send verification code',
        'The verification code was not sent due to some server error. Please retry again later. If the issue persists, contact support@sayify.pro',
      );
    }
  };

  const handleVerifyCode = async () => {
    setUserloading(true);
    let payload = {
      email: user,
      code: code,
    };
    let url = api_config.BASE_BACKEND_ENDPOINT + 'api/verify-code/';
    api_config.ABORT_CONTROLLER = new AbortController();
    const signal = api_config.ABORT_CONTROLLER.signal;

    try {
      UseAPI(signal, url, 'POST', payload)
        .then(function (response) {
          setUserloading(false);
          if (response.code === 400) {
            openNotificationWithIcon(
              'error',
              'Email verification failed',
              'The security code is invalid. Please try again or request a new code',
            );
          }
          if (response.code === 200) {
            setVerifyLoading(false);
            setDisableUser(true);
            setEnableCode(false);
            setEnableSignUp(true);
            setSubmitLoading(true);
            setEnableAPISignUp(true);
            openNotificationWithIcon(
              'success',
              'Verification successful',
              'Your email is now verified successfully. Please enter your new password ',
            );
          }
        })
        .catch(function (err) {
          openNotificationWithIcon(
            'error',
            'Email verification failed',
            'We could not verify your email. Please try again or contact support@sayify.pro ',
          );

          setUserloading(false);
        });
    } catch (err) {
      setUserloading(false);

      openNotificationWithIcon(
        'error',
        'Email verification failed',
        'We could not verify your email. Please try again or contact support@sayify.pro ',
      );
    }
  };

  const handleUpdatePassword = async () => {
    setUserloading(true);
    let payload = {
      email: user,
      code: code,
      password1: pwd,
      password2: matchPwd,
    };
    let url = api_config.BASE_BACKEND_ENDPOINT + 'api/update-password/';
    api_config.ABORT_CONTROLLER = new AbortController();
    const signal = api_config.ABORT_CONTROLLER.signal;

    try {
      UseAPI(signal, url, 'POST', payload)
        .then(function (response) {
          setUserloading(false);
          if (response.code === 200) {
            openNotificationWithIcon(
              'success',
              'Password reset successful',
              'Your password has been changed successfully. Please login with your new password',
            );
            // Redirect to login after a short delay to show the notification
            setTimeout(() => {
              navigate('/login');
            }, 1500);
          } else {
            openNotificationWithIcon(
              'error',
              'Password reset failed',
              response.message || 'Your password was not updated successfully. Please try again or contact support@sayify.pro',
            );
          }
        })
        .catch(function (err) {
          openNotificationWithIcon(
            'error',
            'Password reset failed',
            'Your password was not updated successfully. Please try again or contact support@sayify.pro ',
          );
          setUserloading(false);
        });
    } catch (err) {
      setUserloading(false);

      openNotificationWithIcon(
        'error',
        'Password reset failed',
        'Your password was not updated successfully. Please try again or contact support@sayify.pro ',
      );
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (isVerifyStatus) {
      if (isEnableSignUpAPI) {
        handleUpdatePassword();
      } else {
        handleVerifyCode();
      }
    } else {
      handleAPISubmit();
    }
  };

  return (
    <>
      <NotificationProvider notifications={notifications} onRemove={removeNotification} />
      <AuthContainer
        leftContent={<LPPromoItem1 />}
        logo={<img style={{ width: '140px' }} src={Logo} alt="Sayify.pro" />}
        title="Reset your password"
        subtitle="No worries — enter your email and we'll send you a verification code"
        footerLinks={
          <>
            <p>
              Remember your password?
              <Link to="/login">Back to Login</Link>
            </p>
            <p>
              Don't have an account yet?
              <Link to="/signup">Create new Account</Link>
            </p>
          </>
        }
      >
        <FormContainer className="auth-form" onSubmit={handleSubmit}>
          <FormInputField
            type="text"
            id="username"
            disabled={isDisableduser}
            placeholder="Enter your email"
            autoComplete="off"
            onChange={handleChange}
            value={user}
            required
          />
          {isEnableSignUp && (
            <div>
              <PasswordInputContainer>
                <FormInputField
                  type={showPassword ? "text" : "password"}
                  id="password"
                  placeholder="Enter new password"
                  onChange={(e) => setPwd(e.target.value)}
                  value={pwd}
                  required
                  disabled={isUserLoading}
                  aria-label="New Password"
                />
                <PasswordToggleIcon
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                </PasswordToggleIcon>
              </PasswordInputContainer>
              <PasswordInputContainer>
                <FormInputField
                  type={showMatchPassword ? "text" : "password"}
                  id="confirm_pwd"
                  placeholder="Re-enter new password"
                  onChange={(e) => setMatchPwd(e.target.value)}
                  value={matchPwd}
                  required
                  disabled={isUserLoading}
                  aria-label="Confirm Password"
                />
                <PasswordToggleIcon
                  type="button"
                  onClick={() => setShowMatchPassword(!showMatchPassword)}
                  aria-label={showMatchPassword ? "Hide password" : "Show password"}
                >
                  {showMatchPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                </PasswordToggleIcon>
              </PasswordInputContainer>
            </div>
          )}
          {isEnablecode && (
            <div style={{ marginBottom: '1rem', textAlign: 'center' }}>
              <ReactCodeInput
                type="text"
                className="auth-code"
                inputStyle={{
                  marginRight: '12px',
                  height: '54px',
                  padding: '6px 10px',
                  outline: 'none',
                  fontSize: '24px',
                  border: '2px solid #DDD4CC',
                  borderRadius: '12px',
                  background: '#FCFAF7',
                  color: '#201C18'
                }}
                onChange={handleCode}
                fields={6}
              />
            </div>
          )}
          {errMsg && <p className="error-message">{errMsg}</p>}
          <ResetButton>
            {isVerifyLoading ? (
              <div>
                {isUserLoading && (
                  <Spin
                    indicator={antIcon}
                    style={{
                      color: 'white',
                      marginRight: '10px',
                      marginTop: '3px',
                    }}
                  />
                )}
                <span>Verify Code</span>
              </div>
            ) : isSubmitLoading ? (
              <div>
                {isUserLoading && (
                  <Spin
                    indicator={antIcon}
                    style={{
                      color: 'white',
                      marginRight: '10px',
                      marginTop: '3px',
                    }}
                  />
                )}
                <span>Reset Password</span>
              </div>
            ) : (
              <div>
                {isUserLoading && (
                  <Spin
                    indicator={antIcon}
                    style={{
                      color: 'white',
                      marginRight: '10px',
                      marginTop: '3px',
                    }}
                  />
                )}
                <span>Continue</span>
              </div>
            )}
          </ResetButton>
        </FormContainer>
      </AuthContainer>
    </>
  );
};

export default Reset;
